"use client";

import type { JSONContent } from "@tiptap/core";
import { preloadFlowTableEngineCode } from "@/lib/client/flow-table-code-loader";
import { preloadSupplyPlanningCode } from "@/lib/client/supply-planning-code-loader";
import { preloadChartCode } from "@/lib/client/chart-code-loader";
import { preloadPulseCode, preloadProductHistoryCode } from "./pulse-code-loader";

type WorkspaceEditorModule = typeof import("@/app/workspace/WorkspaceTiptapEditor");

let workspaceEditorPromise: Promise<WorkspaceEditorModule> | null = null;
let loadedWorkspaceEditorModule: WorkspaceEditorModule | null = null;

/** Тот же модуль после прогрева, не экземпляр редактора и не данные страницы.
 * Позволяет не создавать новый цикл Suspense для уже готового компонента. */
export function getLoadedWorkspaceEditorModule(): WorkspaceEditorModule | null {
  return loadedWorkspaceEditorModule;
}

export function loadWorkspaceEditorModule(): Promise<WorkspaceEditorModule> {
  if (!workspaceEditorPromise) {
    workspaceEditorPromise = import("@/app/workspace/WorkspaceTiptapEditor").then(module => {
      loadedWorkspaceEditorModule = module;
      return module;
    }).catch(error => {
      workspaceEditorPromise = null;
      throw error;
    });
  }
  return workspaceEditorPromise;
}

export function preloadWorkspaceEditorCode(): void {
  void loadWorkspaceEditorModule().catch(() => {
    // Прогрев не участвует в успешности навигации; рендер повторит импорт.
  });
}

function documentHasNodeType(doc: JSONContent | null | undefined, expectedType: string): boolean {
  if (!doc || typeof doc !== "object") return false;
  if (doc.type === expectedType) return true;
  return Array.isArray(doc.content) && doc.content.some(node => documentHasNodeType(node, expectedType));
}

/** Редактор нужен каждому документу, тяжёлые модули — по составу блоков. */
export function preloadWorkspaceDocumentCode(doc: JSONContent | null | undefined): void {
  preloadWorkspaceEditorCode();
  if (documentHasNodeType(doc, "flowTable")) preloadFlowTableEngineCode();
  if (documentHasNodeType(doc, "supplyPlanning")) preloadSupplyPlanningCode();
  if (documentHasNodeType(doc, "chartBlock")) preloadChartCode();
  if (documentHasNodeType(doc, "pulseTable")) preloadPulseCode();
  else if (documentHasNodeType(doc, "productHistory")) preloadProductHistoryCode();
}

/** То же условие для прогрева по клику, когда известен серверный план страницы. */
export function preloadWorkspacePlanCode(nodeTypes: readonly string[] | null | undefined): void {
  preloadWorkspaceEditorCode();
  if (nodeTypes?.includes("flowTable")) preloadFlowTableEngineCode();
  if (nodeTypes?.includes("supplyPlanning")) preloadSupplyPlanningCode();
  if (nodeTypes?.includes("chartBlock")) preloadChartCode();
  if (nodeTypes?.includes("pulseTable")) preloadPulseCode();
  else if (nodeTypes?.includes("productHistory")) preloadProductHistoryCode();
}
